import React, { useState, useEffect } from 'react';
import { useUserDataContext } from '../../contexts/UserDataContext';
import { placeOrder } from '../../services/websocketApi/orders';
import { getInstrumentsBySymbol } from '../../services/websocketApi/instruments';
import ClosePositionModal from './ClosePositionModal';
import './Positions.css';
import './PositionsResponsive.css';

/**
 * Positions component using CSS Grid for layout
 */
const Positions = () => {
  const { userData, isSubscribed, isLoading, error } = useUserDataContext();
  const [closingPositionId, setClosingPositionId] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  // Format number with proper decimal places
  const formatNumber = (num, decimals = 2) => {
    if (num === undefined || num === null) return '--';
    const parsedNum = parseFloat(num);
    if (isNaN(parsedNum)) return '--';
    return parsedNum.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Format PnL with sign and color
  const formatPnL = (pnl) => {
    if (pnl === undefined || pnl === null) return { value: '--', isPositive: null };
    const value = parseFloat(pnl);
    if (isNaN(value)) return { value: '--', isPositive: null };
    const formattedValue = Math.abs(value).toLocaleString(undefined, { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return {
      value: `${value >= 0 ? '+' : '-'}$${formattedValue}`,
      isPositive: value >= 0
    };
  };

  // Format symbol
  const formatSymbol = (symbol) => {
    if (!symbol) return '--';
    const parts = symbol.split('.');
    return parts[parts.length - 1];
  };
  
  // Handle position close
  const handleClosePosition = (position) => {
    setSelectedPosition(position);
    setShowConfirmation(true);
  };
  
  // Cancel close confirmation
  const cancelClosePosition = () => {
    setShowConfirmation(false);
    setSelectedPosition(null);
  };
  
  // Handle position closing from modal
  const handlePositionClosed = (orderParams) => {
    if (!selectedPosition) return;
    
    setShowConfirmation(false);
    setClosingPositionId(selectedPosition.tradeId);
    
    // Execute order
    placeOrder(orderParams)
      .then(response => {
        console.log('Position closed:', response);
      })
      .catch(error => {
        console.error('Error closing position:', error);
      })
      .finally(() => {
        setClosingPositionId(null);
        setSelectedPosition(null);
      });
  };

  // Loading state
  if (isLoading) {
    return <div className="positions-message">Loading positions...</div>;
  }
  
  // Error state
  if (error) {
    return <div className="positions-error">Error loading positions: {error}</div>;
  }
  
  // Not subscribed state
  if (!isSubscribed) {
    return <div className="positions-message">Not subscribed to user data</div>;
  }
  
  // No positions state
  if (!userData || !userData.trades || userData.trades.length === 0) {
    return <div className="positions-empty">No open positions</div>;
  }

  return (
    <div className="positions-container">
      {/* Grid Header */}
      <div className="positions-grid-header">
        <div className="positions-grid-cell header-cell col-symbol">Symbol</div>
        <div className="positions-grid-cell header-cell col-side">Side</div>
        <div className="positions-grid-cell header-cell col-size">Size</div>
        <div className="positions-grid-cell header-cell col-entry">Entry</div>
        <div className="positions-grid-cell header-cell col-mark">Mark</div>
        <div className="positions-grid-cell header-cell col-pnl">PnL</div>
        <div className="positions-grid-cell header-cell col-margin">Margin</div>
        <div className="positions-grid-cell header-cell col-leverage">Lev</div>
        <div className="positions-grid-cell header-cell col-actions">Actions</div>
      </div>

      {/* Grid Body */}
      <div className="positions-grid-body">
        {userData.trades.map(position => {
          const pnlInfo = formatPnL(position.pnl);
          const quantityDecimals = Math.abs(position.quantity) >= 1 ? 0 : (Math.abs(position.quantity) >= 0.01 ? 2 : 4);
          return (
            <div key={position.tradeId} className="positions-grid-row">
              <div className="positions-grid-cell data-cell col-symbol">{formatSymbol(position.symbol)}</div>
              <div className="positions-grid-cell data-cell col-side">
                <span className={`side-badge ${position.side === 'buy' ? 'long' : 'short'}`}>
                  {position.side === 'buy' ? 'Long' : 'Short'}
                </span>
              </div>
              <div className="positions-grid-cell data-cell col-size">{formatNumber(position.quantity, quantityDecimals)}</div>
              <div className="positions-grid-cell data-cell col-entry">{formatNumber(position.entryPrice, 5)}</div>
              <div className="positions-grid-cell data-cell col-mark">{formatNumber(position.markPrice || position.entryPrice, 5)}</div>
              <div className="positions-grid-cell data-cell col-pnl">
                <span className={pnlInfo.isPositive === null ? '' : (pnlInfo.isPositive ? 'positive' : 'negative')}>
                  {pnlInfo.value}
                </span>
              </div>
              <div className="positions-grid-cell data-cell col-margin">{formatNumber(position.consumeMargin)}</div>
              <div className="positions-grid-cell data-cell col-leverage">{position.leverage}x</div>
              <div className="positions-grid-cell data-cell col-actions">
                <button 
                  className="btn-close" 
                  onClick={() => handleClosePosition(position)}
                  disabled={closingPositionId === position.tradeId}
                >
                  {closingPositionId === position.tradeId ? 'Closing...' : 'Close'}
                </button>
                {(!position.stopLoss || !position.takeProfit) && (
                  <button className="btn-sl-tp">SL/TP</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Use the new ClosePositionModal component */}
      {showConfirmation && selectedPosition && (
        <ClosePositionModal
          position={selectedPosition}
          onClose={cancelClosePosition}
          onPositionClosed={handlePositionClosed}
          isClosing={closingPositionId === selectedPosition.tradeId}
        />
      )}
    </div>
  );
};

export default Positions; 